import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Stack, Grid, Divider } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { FC, Fragment, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ICandidate, IJob, ILocation, pushSubmissionToDataAnalytics } from 'wisestep-api';
import {
  WsDrawer,
  WsColors,
  WsTypoVariants,
  WsButton,
  WsTypography,
  WsTypoColors,
  ChatBoxHeader,
  WsSelect,
  WsSelectObj,
} from 'wisestep-base';
import { object, string } from 'yup';

import { success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import { trackCV, downloadCV } from '../../conversationWidget/utils/cvHelper';

interface ISendCandidateForAnalytics {
  candidate: ICandidate;
  job: IJob;
  close: () => void;
  sendSuccessfully: (action: boolean) => void;
  openSubmitCandidate: boolean;
}

interface IReviewData {
  emails: string;
}

const schema = object().shape({
  emails: string().required(),
});

const SendCandidateForAnalytics: FC<ISendCandidateForAnalytics> = ({
  candidate,
  job,
  close,
  sendSuccessfully,
  openSubmitCandidate,
}) => {
  const [emailList, setEmailList] = useState<WsSelectObj[]>([
    {
      label: candidate?.email ?? 'N/A',
      value: candidate?.email ?? 'N/A',
    },
  ]);
  const methods = useForm<IReviewData>({
    resolver: yupResolver(schema),
    defaultValues: {
      emails: candidate?.email ?? 'N/A',
    },
  });
  const { handleSubmit, reset } = methods;

  const cvViewSourcePage = useConversationWidgetStore((state) => state.cvViewSourcePage);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const closeModal = () => close();
  const pushCandidateData = async (data: IReviewData) => {
    if (job.jobSource == 'grc' && candidate && candidate.id) {
      const response = await pushSubmissionToDataAnalytics({
        submissionId: candidate.id,
        emailId: data.emails,
      });
      if (response.status == 200) {
        sendSuccessfully(true);
        setNotification(success('Data Pushed.'));
        close();
      } else {
        setNotification(warning('Please try after sometime.'));
      }
    }
  };
  const onReset = () => reset();

  const resumeAction = (action: string, url: string | null) => {
    switch (action) {
      case 'view':
        !isEmpty(url) && trackCV('view', url, cvViewSourcePage);
        break;
      case 'download':
        if (!isEmpty(url)) {
          trackCV('download', url, cvViewSourcePage);
          url && downloadCV(url);
        }
        break;
    }
  };

  const reviewList = [
    { key: 'Name', value: 'name' },
    { key: 'Email', value: 'email' },
    { key: 'Phone Number', value: 'phoneNumber' },
    { key: 'CV', value: 'cv' },
    { key: 'Current Job Title', value: 'currentJobTitle' },
    { key: 'Country', value: 'country' },
    { key: 'Preferred Location', value: 'preferredLocations' },
    { key: 'Skills', value: 'skills' },
    { key: 'Expected Base Salary', value: 'expectedBaseSalary' },
    { key: 'Work Authorization', value: 'workAuthorization' },
  ];
  useEffect(() => {
    if (candidate?.alternateEmails) {
      const alternateEmailList = candidate.alternateEmails.map((email) => ({
        label: email,
        value: email,
      }));
      setEmailList((prevEmailList) => [...prevEmailList, ...alternateEmailList]);
    }
  }, [candidate]);

  return (
    <WsDrawer
      style={{ zIndex: '1300' }}
      size="small"
      hideHeader={true}
      open={openSubmitCandidate}
      onClose={() => closeModal}
      hideOverflow={true}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(pushCandidateData)} onReset={onReset}>
          <Grid container>
            <Grid item xs={12}>
              <ChatBoxHeader>
                <Grid container p={1} spacing={3} direction="row">
                  <Grid item xs={4}>
                    <WsTypography
                      variant={WsTypoVariants.TITLE}
                      color={WsTypoColors.PRIMARY}
                      bold={true}
                    >
                      Review
                    </WsTypography>
                  </Grid>

                  <Grid
                    item
                    xs={8}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <WsTypography variant={WsTypoVariants.BODY1} bold={true}>
                      {`${job?.title ?? ''} ( ${job?.jobCode ?? ''} )`}
                    </WsTypography>
                    <CloseIcon sx={{ cursor: 'pointer', marginLeft: 1 }} onClick={close} />
                  </Grid>
                </Grid>
              </ChatBoxHeader>
            </Grid>
            {reviewList.map((header, index) => (
              <Fragment key={index}>
                <Grid
                  container
                  direction="row"
                  display="flex"
                  justifyContent="space-between"
                  spacing={3}
                  sx={{ margin: '0 0 0 0' }}
                >
                  <Grid
                    item
                    xs={5}
                    sx={{
                      backgroundColor: WsColors.TertiaryShades50,
                      padding: 1,
                    }}
                  >
                    <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                      {header.key}
                    </WsTypography>
                  </Grid>
                  <Grid item xs={7} sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                    {header.key === 'CV' ? (
                      <Stack direction="row" spacing={2} mb={2}>
                        <WsTypography variant={WsTypoVariants.BODY1}>
                          {candidate?.resumeFileOriginalName ?? ''}
                        </WsTypography>
                        <DownloadIcon
                          onClick={() =>
                            resumeAction && resumeAction('download', candidate.resumeLocation)
                          }
                          sx={{
                            color: WsColors.TheBlues500,
                            fontSize: '24px',
                            cursor: 'pointer',
                          }}
                        />
                        <VisibilityIcon
                          onClick={() =>
                            resumeAction && resumeAction('view', candidate.resumeLocation)
                          }
                          sx={{ color: WsColors.TheBlues500, cursor: 'pointer' }}
                        />
                      </Stack>
                    ) : header.key === 'Current Job Title' ? (
                      <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                        {candidate?.currentJobTitle || candidate?.mappedJobTitle || 'N/A'}
                      </WsTypography>
                    ) : header.key === 'Country' ? (
                      <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                        {candidate?.jobLocations?.[0]?.country || 'N/A'}
                      </WsTypography>
                    ) : header.key === 'Email' ? (
                      <Grid item xs={10}>
                        <WsSelect
                          name="emails"
                          size="small"
                          selectArray={emailList}
                          defaultValue={candidate?.email ?? undefined}
                        />
                      </Grid>
                    ) : header.key === 'Preferred Location' ? (
                      <Grid item xs={10}>
                        <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                          {candidate?.preferredLocations
                            ?.map((location: ILocation) => location?.country)
                            .join(', ') || 'N/A'}
                        </WsTypography>
                      </Grid>
                    ) : (
                      <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                        {candidate?.[header.value] || 'N/A'}
                      </WsTypography>
                    )}
                  </Grid>
                </Grid>
                <Grid xs={12}>
                  <Divider />
                </Grid>
              </Fragment>
            ))}
          </Grid>
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            margin={2}
            justifyContent="flex-end"
          >
            <WsButton
              onClick={() => {
                close();
              }}
            >
              Cancel
            </WsButton>
            <WsButton type="submit">Send</WsButton>
          </Stack>
        </form>
      </FormProvider>
    </WsDrawer>
  );
};

export default SendCandidateForAnalytics;
